import { Dialog } from "@headlessui/react";
import { AiOutlineClose } from "react-icons/ai";
import AssociationsDisplay from "./AssociationsDisplay";
import PillarGraphic from "./PillarGraphic";
import ProjectPublicationList from "./ProjectPublicationList";
import { getPillarContent } from "../utils/pillarContent";
import { associationTypes } from "../data/associationTypes";
import { formatHtml } from "../utils/formatHtml";

const LinkedItemsSection = ({ title, items = [] }) => {
  if (!items.length) return null;

  return (
    <section className="mt-5 sm:mt-6">
      <h3 className="text-sm font-semibold text-lightText dark:text-darkText">{title}</h3>
      <ul className="mt-2 space-y-3.5 sm:space-y-3">
        {items.map((item, idx) => (
          <li
            key={`${item.title || item.date}-${idx}`}
            className="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
          >
            {item.date ? (
              <p className="mb-0.5 text-sm font-semibold text-gray-500 dark:text-gray-400 sm:mb-0 sm:mr-2 sm:inline">
                {item.date}
              </p>
            ) : null}
            {/<a\s/i.test(item.title || "") ? (
              <span
                className="[&_a]:text-green dark:[&_a]:text-green-dark [&_a]:underline [&_a]:underline-offset-2"
                dangerouslySetInnerHTML={{
                  __html: formatHtml(item.title),
                }}
              />
            ) : (
              <span>{item.title}</span>
            )}
            {item.links?.slides ? (
              <>
                {" "}
                <a
                  href={item.links.slides}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green dark:text-green-dark underline underline-offset-2"
                >
                  Slides
                </a>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

const PillarModal = ({ pillarId, onClose }) => {
  const content = pillarId ? getPillarContent(pillarId) : null;
  const isOpen = Boolean(pillarId);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-hidden"
    >
      <div className="flex h-full items-end justify-center sm:items-center sm:p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-[1px]" />

        <div className="relative z-10 flex h-[92dvh] max-h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-lightBg shadow-xl dark:border-gray-700 dark:bg-darkBg sm:h-[75vh] sm:max-h-[75vh] sm:w-[min(100%,75vw)] sm:rounded-xl">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:items-start sm:px-6">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {content && (
                <PillarGraphic
                  pillarId={content.pillar.id}
                  className="mt-0.5 hidden h-[4.25rem] w-[5.75rem] shrink-0 sm:block"
                />
              )}
              <div className="min-w-0">
                {content ? (
                  <>
                    <p className="text-sm font-semibold tracking-wide text-green dark:text-green-dark">
                      {content.pillar.bigGoal}
                    </p>
                    {/* Full question stays in the scroll body on mobile to keep the header compact */}
                    <p className="mt-1 hidden text-lg font-semibold leading-snug text-lightText dark:text-darkText sm:block sm:text-xl">
                      {content.pillar.title}
                    </p>
                  </>
                ) : (
                  <Dialog.Title className="text-lg font-semibold text-lightText dark:text-darkText">
                    Pillar not found
                  </Dialog.Title>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-mr-1 shrink-0 rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-green dark:hover:text-green-dark dark:hover:bg-gray-800"
              aria-label="Close"
            >
              <AiOutlineClose size={18} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-5">
            {!content ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This research pillar could not be found.
              </p>
            ) : (
              <>
                <Dialog.Title className="mb-4 text-base font-semibold leading-snug text-lightText dark:text-darkText sm:sr-only">
                  {content.pillar.title}
                </Dialog.Title>

                {content.pillar.description && (
                  <div className="rounded-lg border border-green/25 border-l-4 border-l-green bg-green/5 px-3.5 py-3 dark:bg-green/10 sm:px-4">
                    <p className="text-sm font-semibold uppercase tracking-wide text-green dark:text-green-dark">
                      Overview
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {content.pillar.description}
                    </p>
                  </div>
                )}

                <AssociationsDisplay
                  className="mt-4"
                  associations={content.pillar.associations}
                  associationTypes={associationTypes}
                  excludeTypeIds={[
                    "pillars",
                    "projects",
                    "publications",
                    "news",
                    "features",
                  ]}
                />

                <ProjectPublicationList
                  title="Publications / Papers"
                  items={content.relatedPublications}
                />
                <ProjectPublicationList
                  title="Workshops"
                  items={content.workshops}
                  showWorkshopTitle
                />
                <LinkedItemsSection
                  title="Talks and Presentations"
                  items={content.talks}
                />
                <LinkedItemsSection
                  title="Media Coverage"
                  items={content.mediaCoverage}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default PillarModal;
